import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const leaveApi = createApi({
  reducerPath: 'leaveApi',
  baseQuery,
  tagTypes: ['Leave', 'LeaveType', 'LeaveBalance'],
  endpoints: (builder) => ({
    // Leave requests
    getLeaveRequests: builder.query({
      query: (params) => ({
        url: '/leave/requests',
        params,
      }),
      providesTags: ['Leave'],
    }),
    getLeaveRequest: builder.query({
      query: (id) => `/leave/requests/${id}`,
      providesTags: (result, error, id) => [{ type: 'Leave', id }],
    }),
    createLeaveRequest: builder.mutation({
      query: (leaveData) => ({
        url: '/leave/requests',
        method: 'POST',
        body: leaveData,
      }),
      invalidatesTags: ['Leave', 'LeaveBalance'],
    }),
    updateLeaveRequest: builder.mutation({
      query: ({ id, ...leaveData }) => ({
        url: `/leave/requests/${id}`,
        method: 'PUT',
        body: leaveData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Leave', id }],
    }),
    cancelLeaveRequest: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/leave/requests/${id}/cancel`,
        method: 'PATCH',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Leave', id }, 'LeaveBalance'],
    }),
    
    // Leave approvals
    approveLeaveRequest: builder.mutation({
      query: ({ id, remarks }) => ({
        url: `/leave/requests/${id}/approve`,
        method: 'PATCH',
        body: { remarks },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Leave', id }, 'LeaveBalance'],
    }),
    rejectLeaveRequest: builder.mutation({
      query: ({ id, remarks }) => ({
        url: `/leave/requests/${id}/reject`,
        method: 'PATCH',
        body: { remarks },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Leave', id }],
    }),
    
    // Leave balances
    getLeaveBalances: builder.query({
      query: ({ employeeId, year }) => ({
        url: `/leave/balances/${employeeId}`,
        params: { year },
      }),
      providesTags: ['LeaveBalance'],
    }),
    updateLeaveBalance: builder.mutation({
      query: ({ employeeId, leaveTypeId, ...balanceData }) => ({
        url: `/leave/balances/${employeeId}/${leaveTypeId}`,
        method: 'PUT',
        body: balanceData,
      }),
      invalidatesTags: ['LeaveBalance'],
    }),
    
    // Leave types
    getLeaveTypes: builder.query({
      query: () => '/leave/types',
      providesTags: ['LeaveType'],
    }),
    createLeaveType: builder.mutation({
      query: (leaveTypeData) => ({
        url: '/leave/types',
        method: 'POST',
        body: leaveTypeData,
      }),
      invalidatesTags: ['LeaveType'],
    }),
    updateLeaveType: builder.mutation({
      query: ({ id, ...leaveTypeData }) => ({
        url: `/leave/types/${id}`,
        method: 'PUT',
        body: leaveTypeData,
      }),
      invalidatesTags: ['LeaveType'],
    }),
    deleteLeaveType: builder.mutation({
      query: (id) => ({
        url: `/leave/types/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LeaveType'],
    }),
    
    // Leave accrual
    accrueLeaves: builder.mutation({
      query: (accrualData) => ({
        url: '/leave/accrue',
        method: 'POST',
        body: accrualData,
      }),
      invalidatesTags: ['LeaveBalance'],
    }),

    // Carry forward operations
    carryForwardLeaveBalances: builder.mutation({
      query: (carryForwardData) => ({
        url: '/leave/carry-forward',
        method: 'POST',
        body: carryForwardData,
      }),
      invalidatesTags: ['LeaveBalance'],
    }),
    getCarryForwardHistory: builder.query({
      query: (params) => ({
        url: '/leave/carry-forward-history',
        params,
      }),
      providesTags: ['LeaveBalance'],
    }),
    validateCarryForward: builder.mutation({
      query: (validationData) => ({
        url: '/leave/validate-carry-forward',
        method: 'POST',
        body: validationData,
      }),
    }),
    
    // Leave calendar
    getLeaveCalendar: builder.query({
      query: (params) => ({
        url: '/leave/calendar',
        params,
      }),
      providesTags: ['Leave'],
    }),
    
    // Leave reports
    getLeaveReport: builder.query({
      query: (params) => ({
        url: '/leave/reports',
        params,
      }),
      providesTags: ['Leave'],
    }),
    exportLeaveReport: builder.mutation({
      query: (params) => ({
        url: '/leave/reports/export',
        method: 'POST',
        body: params,
      }),
    }),
    
    // Holidays
    getHolidays: builder.query({
      query: (year) => ({
        url: '/leave/holidays',
        params: { year },
      }),
      providesTags: ['Leave'],
    }),
    createHoliday: builder.mutation({
      query: (holidayData) => ({
        url: '/leave/holidays',
        method: 'POST',
        body: holidayData,
      }),
      invalidatesTags: ['Leave'],
    }),
    updateHoliday: builder.mutation({
      query: ({ id, ...holidayData }) => ({
        url: `/leave/holidays/${id}`,
        method: 'PUT',
        body: holidayData,
      }),
      invalidatesTags: ['Leave'],
    }),
    deleteHoliday: builder.mutation({
      query: (id) => ({
        url: `/leave/holidays/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Leave'],
    }),
  }),
});

export const {
  useGetLeaveRequestsQuery,
  useGetLeaveRequestQuery,
  useCreateLeaveRequestMutation,
  useUpdateLeaveRequestMutation,
  useCancelLeaveRequestMutation,
  useApproveLeaveRequestMutation,
  useRejectLeaveRequestMutation,
  useGetLeaveBalancesQuery,
  useUpdateLeaveBalanceMutation,
  useGetLeaveTypesQuery,
  useCreateLeaveTypeMutation,
  useUpdateLeaveTypeMutation,
  useDeleteLeaveTypeMutation,
  useAccrueLeavesMutation,
  useCarryForwardLeaveBalancesMutation,
  useGetCarryForwardHistoryQuery,
  useValidateCarryForwardMutation,
  useGetLeaveCalendarQuery,
  useGetLeaveReportQuery,
  useExportLeaveReportMutation,
  useGetHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
} = leaveApi;
